import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
// import AIFormGeneratorService from './aiFormGeneratorService'; // AI disabled

// PDF.js worker ayarı
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export class DocumentParserService {
  constructor() {
    this.fieldIdCounter = 1;
    this.useAI = false; // AI disabled temporarily
  }

  // Alan tipini tahmin et (iyileştirilmiş)
  detectFieldType(label, value = '') {
    if (!label) return 'text';
    
    // label ve value'yu string'e çevir
    const labelStr = typeof label === 'string' ? label : String(label);
    const valueStr = typeof value === 'string' ? value : String(value);
    
    const labelLower = labelStr.toLowerCase().trim();
    const valueLower = valueStr.toLowerCase().trim();
    
    // Türkçe alan isimleri ve İngilizce karşılıkları
    const fieldPatterns = {
      email: ['email', 'e-mail', 'e-posta', 'eposta', 'elektronik', 'mail'],
      phone: ['telefon', 'phone', 'mobil', 'gsm', 'tel', 'cep'],
      date: ['tarih', 'date', 'doğum', 'birth', 'başla', 'bitir', 'son'],
      time: ['saat', 'time', 'zaman', 'hour'],
      number: ['yaş', 'age', 'miktar', 'adet', 'sayı', 'number', 'fiyat', 'tutar', 'ücret'],
      textarea: ['açıklama', 'description', 'detay', 'not', 'comment', 'mesaj', 'adres', 'address'],
      select: ['seç', 'choose', 'liste', 'list', 'kategori', 'tür', 'tip'],
      checkbox: ['seçenek', 'option', 'çoklu', 'multiple'],
      radio: ['cinsiyet', 'gender', 'durum', 'status', 'tür', 'type']
    };
    
    // Pattern matching
    for (const [type, patterns] of Object.entries(fieldPatterns)) {
      if (patterns.some(pattern => labelLower.includes(pattern))) {
        // Seçenekli alanlar için value kontrolü
        if (type === 'radio' || type === 'checkbox') {
          if (this.hasOptions(valueStr)) {
            return type;
          }
          return 'select'; // Seçenek bulunamadıysa select yap
        }
        return type;
      }
    }
    
    // Value pattern kontrolü
    if (this.isEmailValue(valueStr)) return 'email';
    if (this.isPhoneValue(valueStr)) return 'phone';
    if (this.isDateValue(valueStr)) return 'date';
    if (this.isNumberValue(valueStr)) return 'number';
    if (this.hasOptions(valueStr)) return 'radio';
    
    // Uzun metin kontrolü
    if (labelLower.length > 50 || valueStr.length > 100) return 'textarea';
    
    // Varsayılan
    return 'text';
  }

  // Yardımcı fonksiyonlar
  isEmailValue(value) {
    return /\b[\w._%+-]+@[\w.-]+\.[A-Z|a-z]{2,}\b/.test(value);
  }

  isPhoneValue(value) {
    return /[\d\s\-\+\(\)]{10,}/.test(value);
  }

  isDateValue(value) {
    return /\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}/.test(value);
  }

  isNumberValue(value) {
    return /^\d+$/.test(value.trim());
  }

  hasOptions(value) {
    const optionPatterns = ['☐', '☑', '○', '●', '□', '■', '[]', '()', 'evet', 'hayır', 'erkek', 'kadın'];
    return optionPatterns.some(pattern => value.includes(pattern));
  }

  // AI ile dokuman parse et (basit ve hızlı)
  async parseWithAI(documentText, fileName) {
    try {
      // Sadece temel bilgileri iste, karmaşık analiz yapma
      const prompt = `Bu belgeden form alanlarını listele:

${documentText.substring(0, 1000)}

JSON format:
{
  "title": "Form adı",
  "fields": [
    {"type": "text", "label": "Alan adı", "required": false}
  ]
}

Alan tipleri: text, email, phone, number, date, textarea, select, radio, checkbox

Kısa ve net JSON döndür.`;

      const aiResult = await AIFormGeneratorService.generateForm(prompt);
      
      return {
        title: aiResult.formTitle || 'AI Form',
        fields: aiResult.formFields.map(field => ({
          ...field,
          name: this.generateFieldName(field.label)
        }))
      };
    } catch (error) {
      console.error('AI parse error:', error);
      return null;
    }
  }
  cleanLabel(text) {
    if (!text) return '';
    // text'i string'e çevir
    const textStr = typeof text === 'string' ? text : String(text);
    return textStr
      .replace(/[:\*\-\_\|]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^\w/, c => c.toUpperCase());
  }

  // Required kontrolü
  isRequired(text) {
    if (!text) return false;
    // text'i string'e çevir
    const textStr = typeof text === 'string' ? text : String(text);
    const required = textStr.includes('*') || 
                    textStr.toLowerCase().includes('zorunlu') || 
                    textStr.toLowerCase().includes('required') ||
                    textStr.toLowerCase().includes('mandatory') ||
                    textStr.toLowerCase().includes('gerekli');
    return required;
  }

  // Seçenekleri çıkar
  extractOptions(text) {
    const options = [];
    const patterns = [
      /☐\s*([^☐☑]+)/g,
      /☑\s*([^☐☑]+)/g,
      /○\s*([^○●]+)/g,
      /●\s*([^○●]+)/g,
      /□\s*([^□■]+)/g,
      /■\s*([^□■]+)/g,
      /\[\s*\]\s*([^\[\]]+)/g,
      /\[x\]\s*([^\[\]]+)/g,
      /\(\s*\)\s*([^\(\)]+)/g,
      /\(x\)\s*([^\(\)]+)/g
    ];
    
    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const option = match[1].trim();
        if (option && !options.find(o => o.value === option)) {
          options.push({
            id: `option_${Date.now()}_${options.length}`,
            value: option.toLowerCase().replace(/\s+/g, '_'),
            label: option
          });
        }
      }
    });
    
    // Eğer hiç seçenek bulunamadıysa varsayılan seçenekler ekle
    if (options.length === 0) {
      const defaultOptions = [
        { id: `option_${Date.now()}_1`, value: 'evet', label: 'Evet' },
        { id: `option_${Date.now()}_2`, value: 'hayir', label: 'Hayır' }
      ];
      return defaultOptions;
    }
    
    return options;
  }

  // Alan name'i oluştur
  generateFieldName(label) {
    if (!label) return `field_${this.fieldIdCounter++}`;
    
    // label'i string'e çevir
    const labelStr = typeof label === 'string' ? label : String(label);
    
    return labelStr
      .toLowerCase()
      .replace(/[ğüşıöçĞÜŞİÖÇ]/g, (char) => {
        const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ı': 'i', 'ö': 'o', 'ç': 'c',
                     'Ğ': 'G', 'Ü': 'U', 'Ş': 'S', 'İ': 'I', 'Ö': 'O', 'Ç': 'C' };
        return map[char] || char;
      })
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  // Form field objesi oluştur
  createFormField(label, type, value = '', required = false) {
    const fieldId = `field_${Date.now()}_${this.fieldIdCounter++}`;
    const fieldName = this.generateFieldName(label);
    
    const field = {
      id: fieldId,
      name: fieldName,
      type: type,
      label: label,
      placeholder: this.generatePlaceholder(label, type),
      required: required,
      validation: {},
      page: 1
    };

    // Tip özelinde ek özellikler
    switch (type) {
      case 'radio':
      case 'checkbox':
      case 'select':
        field.options = this.extractOptions(value);
        break;
      case 'number':
        field.min = 0;
        field.max = 999999;
        break;
      case 'email':
        field.validation.pattern = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';
        field.validation.errorMessage = 'Geçerli bir e-posta adresi girin';
        break;
      case 'phone':
        field.validation.pattern = '^[0-9]{10}$';
        field.validation.errorMessage = 'Geçerli bir telefon numarası girin';
        break;
      case 'url':
        field.validation.pattern = '^https?:\\/\\/.*';
        field.validation.errorMessage = 'Geçerli bir web adresi girin';
        break;
      case 'textarea':
        field.rows = 4;
        field.maxLength = 1000;
        break;
      case 'date':
        field.format = 'DD/MM/YYYY';
        break;
      case 'time':
        field.format = 'HH:mm';
        break;
      case 'currency':
        field.currency = 'TRY';
        field.min = 0;
        field.max = 1000000;
        break;
      case 'file':
        field.maxFileSize = 10485760; // 10MB
        field.maxFiles = 1;
        field.allowMultiple = false;
        break;
      case 'image':
        field.maxFileSize = 5242880; // 5MB
        field.maxFiles = 5;
        field.allowMultiple = false;
        break;
      case 'pdf':
        field.maxFileSize = 20971520; // 20MB
        field.maxFiles = 1;
        field.allowMultiple = false;
        break;
    }

    return field;
  }

  // Placeholder oluştur
  generatePlaceholder(label, type) {
    switch (type) {
      case 'email':
        return 'ornek@email.com';
      case 'phone':
        return '05XX XXX XX XX';
      case 'date':
        return 'GG/AA/YYYY';
      case 'time':
        return 'SS:DD';
      case 'currency':
        return '0,00 ₺';
      case 'url':
        return 'https://example.com';
      case 'number':
        return '0';
      default:
        return `${label} giriniz`;
    }
  }

  // Excel dosyası parse et
  async parseExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          const formData = this.extractFormFromTable(jsonData);
          resolve(formData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Word dosyası parse et (HTML olarak)
  async parseWord(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const formData = this.extractFormFromHTML(content);
          resolve(formData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // PDF dosyası parse et
  async parsePDF(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const typedarray = new Uint8Array(e.target.result);
          const pdf = await pdfjsLib.getDocument(typedarray).promise;
          let fullText = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n';
          }
          
          const formData = this.extractFormFromText(fullText);
          resolve(formData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Tablodan form çıkar (iyileştirilmiş)
  extractFormFromTable(data) {
    const fields = [];
    let title = 'İçe Aktarılan Form';
    
    if (!data || data.length === 0) {
      return { title, fields };
    }
    
    // İlk satır başlık olabilir
    if (data.length > 0 && data[0].length === 1 && data[0][0]) {
      title = this.cleanLabel(data[0][0]);
      data = data.slice(1);
    }
    
    const processedLabels = new Set(); // Tekrar eden alanları önle
    
    data.forEach((row, index) => {
      if (row.length >= 1 && row[0] && row[0].toString().trim()) {
        const rawLabel = row[0].toString().trim();
        const label = this.cleanLabel(rawLabel);
        const value = row[1] ? row[1].toString().trim() : '';
        
        // Boş veya çok kısa etiketleri atla
        if (!label || label.length < 2) return;
        
        // Tekrar eden etiketleri atla
        const labelKey = label.toLowerCase();
        if (processedLabels.has(labelKey)) return;
        processedLabels.add(labelKey);
        
        // Başlık satırlarını atla
        if (this.isHeaderRow(label)) return;
        
        const required = this.isRequired(rawLabel);
        const type = this.detectFieldType(label, value);
        
        const field = this.createFormField(label, type, value, required);
        fields.push(field);
      }
    });
    
    return { title, fields };
  }

  // Başlık satırı kontrolü
  isHeaderRow(label) {
    const headerWords = ['form', 'başlık', 'title', 'header', 'alan', 'field', 'bilgi', 'info'];
    const labelLower = label.toLowerCase();
    return headerWords.some(word => labelLower.includes(word)) && label.length < 15;
  }

  // HTML'den form çıkar
  extractFormFromHTML(html) {
    const fields = [];
    let title = 'İçe Aktarılan Word Belgesi';
    
    // Başlık bul
    const titleMatch = html.match(/<h[1-6][^>]*>([^<]+)<\/h[1-6]>/i);
    if (titleMatch) {
      title = this.cleanLabel(titleMatch[1]);
    }
    
    // Form alanları için kalıplar
    const patterns = [
      /<label[^>]*>([^<]+)<\/label>/gi,
      /<input[^>]*placeholder="([^"]+)"/gi,
      /([A-Za-zığüşöçİĞÜŞÖÇ\s]+):\s*_+/g,
      /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*\[\s*\]/g,
      /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*\.\.\./g,
      /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*:\s*$/gm
    ];
    
    let matches = [];
    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(html)) !== null) {
        if (match[1] && match[1].trim()) {
          matches.push({
            label: match[1].trim(),
            context: match[0]
          });
        }
      }
    });
    
    // Benzersiz alanları filtrele
    const uniqueLabels = new Set();
    matches.forEach((match, index) => {
      const cleanedLabel = this.cleanLabel(match.label);
      if (cleanedLabel && !uniqueLabels.has(cleanedLabel.toLowerCase())) {
        uniqueLabels.add(cleanedLabel.toLowerCase());
        
        const required = this.isRequired(match.context);
        const type = this.detectFieldType(cleanedLabel, match.context);
        
        const field = this.createFormField(cleanedLabel, type, match.context, required);
        fields.push(field);
      }
    });
    
    return { title, fields };
  }

  // Metinden form çıkar
  extractFormFromText(text) {
    const fields = [];
    let title = 'İçe Aktarılan PDF Belgesi';
    
    const lines = text.split('\n').filter(line => line.trim());
    
    // İlk satır başlık olabilir
    if (lines.length > 0 && lines[0].length < 100 && !lines[0].includes(':')) {
      title = this.cleanLabel(lines[0]);
      lines.shift();
    }
    
    const processedLabels = new Set();
    
    lines.forEach((line) => {
      // Form alanı kalıpları
      const patterns = [
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+):\s*_+/,
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*\[\s*\]/,
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+):\s*$/,
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*\.\.\./,
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*━+/,
        /([A-Za-zığüşöçİĞÜŞÖÇ\s]+)\s*─+/
      ];
      
      for (let pattern of patterns) {
        const match = line.match(pattern);
        if (match && match[1]) {
          const label = this.cleanLabel(match[1]);
          
          if (label && !processedLabels.has(label.toLowerCase())) {
            processedLabels.add(label.toLowerCase());
            
            const required = this.isRequired(line);
            const type = this.detectFieldType(label, line);
            
            const field = this.createFormField(label, type, line, required);
            fields.push(field);
            break;
          }
        }
      }
    });
    
    return { title, fields };
  }

  // Ana parse metodu
  async parseDocument(file) {
    const fileName = file.name.toLowerCase();
    
    try {
      // Önce hızlı klasik yöntemle parse et
      let classicResult = null;
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        classicResult = await this.parseExcel(file);
      } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
        classicResult = await this.parseWord(file);
      } else if (fileName.endsWith('.pdf')) {
        classicResult = await this.parsePDF(file);
      } else {
        throw new Error('Desteklenmeyen dosya formatı. Lütfen Excel, Word veya PDF dosyası yükleyin.');
      }
      
      // Eğer yeterli alan bulunduysa klasik sonucu döndür (hızlı)
      if (classicResult && classicResult.fields && classicResult.fields.length >= 2) {
        console.log('✅ Klasik yöntem yeterli sonuç verdi, AI kullanılmıyor');
        return classicResult;
      }
      
      // Sadece çok az alan bulunursa AI'ı dene (yavaş ama kapsamlı)
      console.log('🤖 Az alan bulundu, AI ile zenginleştiriliyor...');
      try {
        const documentText = await this.extractTextContent(file, fileName);
        if (documentText && documentText.trim().length > 100) {
          const aiResult = await this.parseWithAI(documentText, file.name);
          if (aiResult && aiResult.fields && aiResult.fields.length > classicResult.fields.length) {
            console.log('✅ AI daha iyi sonuç verdi');
            return aiResult;
          }
        }
      } catch (aiError) {
        console.warn('AI parse başarısız:', aiError.message);
      }
      
      return classicResult;
    } catch (error) {
      console.error('Dosya parse edilirken hata:', error);
      throw new Error(`Dosya işlenirken hata oluştu: ${error.message}`);
    }
  }

  // Hızlı metin çıkarma
  async extractTextContent(file, fileName) {
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return await this.extractTextFromExcel(file);
    } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
      return await this.extractTextFromWord(file);
    } else if (fileName.endsWith('.pdf')) {
      return await this.extractTextFromPDF(file);
    }
    return '';
  }

  // PDF'den metin çıkar (AI için)
  async extractTextFromPDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const typedarray = new Uint8Array(arrayBuffer);
      const pdf = await pdfjsLib.getDocument(typedarray).promise;
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (error) {
      console.error('PDF text extraction error:', error);
      return '';
    }
  }

  // Word'den metin çıkar (AI için)  
  async extractTextFromWord(file) {
    try {
      const text = await file.text();
      return text;
    } catch (error) {
      console.error('Word text extraction error:', error);
      return '';
    }
  }

  // Excel'den metin çıkar (AI için)
  async extractTextFromExcel(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Her satırı temizle ve birleştir
      const cleanText = jsonData
        .map(row => row.filter(cell => cell !== null && cell !== undefined && cell !== ''))
        .filter(row => row.length > 0)
        .map(row => row.join(' '))
        .join('\n');
      
      return cleanText;
    } catch (error) {
      console.error('Excel text extraction error:', error);
      return '';
    }
  }
}

// Singleton instance
const documentParserService = new DocumentParserService();
export default documentParserService;
